import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ColumnPinningState,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { Columns3, Download, Layers, Loader2, RefreshCw, RotateCcw, Rows3, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Kbd } from '../../components/ui/kbd';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { CatalogueApiError, CatalogueRow, fetchStyles, fetchVariantRows, StyleRow } from '../../lib/catalogueAdminApi';
import { buildColumns, DEFAULT_HIDDEN, globalSearch, rowsToCsv } from '../../components/admin/catalogue/columns';
import { CatalogueTable } from '../../components/admin/catalogue/CatalogueTable';
import { FilterBar } from '../../components/admin/catalogue/FilterBar';
import { SelectionBar } from '../../components/admin/catalogue/SelectionBar';
import { TablePrefs, useTablePrefs, useTableUrlState } from '../../components/admin/catalogue/useTableUrlState';
import { count } from '../../components/admin/catalogue/format';
import { ToolButton } from '../../components/admin/dash/Toolbar';

const PREF_DEFAULTS: TablePrefs = { columnVisibility: DEFAULT_HIDDEN, columnSizing: {}, columnOrder: [], density: 'compact', pageSize: 100 };
const PINNED: ColumnPinningState = { left: ['select', 'product'] };

const download = (name: string, text: string) => {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

const Catalogue: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const searchRef = useRef<HTMLInputElement>(null);

  const [data, setData] = useState<StyleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingStyles, setLoadingStyles] = useState<Set<string>>(new Set());
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(PINNED);
  const [editingFilter, setEditingFilter] = useState<string | null>(null);

  const url = useTableUrlState();
  const { prefs, onColumnVisibilityChange, onColumnSizingChange, onColumnOrderChange, onPaginationChange, setDensity, resetLayout } = useTablePrefs(PREF_DEFAULTS);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/admin/login', { replace: true });
  }, [authLoading, isAuthenticated, navigate]);

  // "/" focuses search anywhere on the page.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (e.key === '/' && !typing) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const load = useCallback(async (force = false) => {
    setLoading(true);
    try {
      setData((await fetchStyles({ force })).filter((s) => s.is_live));
    } catch (err: any) {
      if (err instanceof CatalogueApiError && err.status === 401) {
        navigate('/admin/login', { replace: true });
        return;
      }
      toast.error(err?.message || 'Could not load the catalogue');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (isAuthenticated) load();
  }, [isAuthenticated, load]);

  // SKUs are fetched the first time a style is expanded and attached as its sub-rows.
  const loadVariants = useCallback(async (styleCode: string) => {
    setLoadingStyles((s) => new Set(s).add(styleCode));
    try {
      const variants = await fetchVariantRows(styleCode);
      setData((old) => old.map((s) => (s.style_code === styleCode ? { ...s, variants } : s)));
    } catch (err: any) {
      toast.error(err?.message || `Could not load SKUs for ${styleCode}`);
    } finally {
      setLoadingStyles((s) => {
        const next = new Set(s);
        next.delete(styleCode);
        return next;
      });
    }
  }, []);

  const columns = useMemo(() => buildColumns(), []);
  const pagination = useMemo(() => ({ pageIndex: url.pageIndex, pageSize: prefs.pageSize }), [url.pageIndex, prefs.pageSize]);

  const table = useReactTable<CatalogueRow>({
    data,
    columns,
    state: {
      globalFilter: url.globalFilter,
      columnFilters: url.columnFilters,
      sorting: url.sorting,
      grouping: url.grouping,
      pagination,
      rowSelection,
      expanded,
      columnVisibility: prefs.columnVisibility,
      columnSizing: prefs.columnSizing,
      columnOrder: prefs.columnOrder,
      columnPinning,
    },
    globalFilterFn: globalSearch,
    onGlobalFilterChange: (u) => url.setGlobalFilter(typeof u === 'function' ? u(url.globalFilter) : u),
    onColumnFiltersChange: url.onColumnFiltersChange,
    onSortingChange: url.onSortingChange,
    onGroupingChange: url.onGroupingChange,
    onPaginationChange: (u) => onPaginationChange(u, pagination, url.setPageIndex),
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    onColumnVisibilityChange,
    onColumnSizingChange,
    onColumnOrderChange,
    onColumnPinningChange: setColumnPinning,
    getRowId: (row) => (row.kind === 'style' ? row.style_code : `sku:${row.sku_code}`),
    // Style rows expand to one sub-row per SKU, loaded on first expand.
    getSubRows: (row) => (row.kind === 'style' ? row.variants : undefined),
    getRowCanExpand: (row) => row.original.kind === 'style',
    // Filters judge style rows only; their SKU sub-rows follow the parent.
    maxLeafRowFilterDepth: 0,
    paginateExpandedRows: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    columnResizeMode: 'onChange',
    enableRowSelection: true,
    enableSubRowSelection: true,
    groupedColumnMode: false,
    autoResetPageIndex: false,
    autoResetExpanded: false,
  });

  const selectedCodes = useMemo(() => Object.keys(rowSelection).filter((k) => rowSelection[k] && !k.includes(':')), [rowSelection]);
  const exportRows = (onlySelected: boolean) => {
    const rows = table.getSortedRowModel().flatRows.filter((r) => !r.getIsGrouped() && r.original.kind === 'style' && (!onlySelected || rowSelection[r.id]));
    download(`catalogue-${new Date().toISOString().slice(0, 10)}.csv`, rowsToCsv(rows));
    toast.success(`Exported ${count(rows.length)} styles`);
  };
  const grouping = table.getState().grouping[0] ?? '';
  const groupable = table.getAllLeafColumns().filter((c) => c.columnDef.meta?.groupable);
  const hideable = table.getAllLeafColumns().filter((c) => c.getCanHide());

  if (authLoading || !isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[color:var(--oa-raised)] text-[color:var(--oa-muted)]">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-[color:var(--oa-card)] font-poppins text-[color:var(--oa-text)] antialiased">
      {/* Phones: title and tools (scrolling sideways) on one row, search full width beneath. */}
      <header className="flex min-h-14 flex-wrap items-center gap-3 border-b border-[color:var(--oa-line-2)] px-4 max-[700px]:gap-y-2.5 max-[700px]:py-2.5">
        <h1 className="font-montserrat text-[17px] font-extrabold tracking-[-0.01em] text-[color:var(--oa-text)]">Catalogue</h1>

        <div className="relative ml-4 w-[360px] max-w-[40vw] max-[700px]:order-3 max-[700px]:ml-0 max-[700px]:w-full max-[700px]:max-w-none">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--oa-muted)]" />
          <input
            ref={searchRef}
            value={url.globalFilter}
            onChange={(e) => url.setGlobalFilter(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                url.setGlobalFilter('');
                (e.target as HTMLInputElement).blur();
              }
            }}
            placeholder="Search name, code, brand, type"
            className="h-8 w-full rounded-md border border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] pl-8 pr-8 text-[13px] text-[color:var(--oa-text)] placeholder:text-[color:var(--oa-faint)] outline-none transition-colors focus:border-[color:var(--oa-accent)] focus:bg-[color:var(--oa-card)] focus:ring-2 focus:ring-[color:color-mix(in_oklab,var(--oa-accent)_20%,transparent)]"
          />
          <div className="absolute right-1.5 top-1/2 -translate-y-1/2">
            {url.globalFilter ? (
              <button type="button" onClick={() => url.setGlobalFilter('')} className="rounded p-0.5 text-[color:var(--oa-muted)] hover:bg-[color:var(--oa-raised-2)] hover:text-[color:var(--oa-text)]" aria-label="Clear search"><X className="h-3.5 w-3.5" /></button>
            ) : (
              <Kbd className="bg-[color:var(--oa-raised-2)] text-[color:var(--oa-muted)]">/</Kbd>
            )}
          </div>
        </div>

        <div className="ml-auto flex items-center gap-1.5 max-[700px]:min-w-0 max-[700px]:flex-1 max-[700px]:overflow-x-auto max-[700px]:[scrollbar-width:none] max-[700px]:[&>*]:shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <ToolButton active={!!grouping}><Layers className="h-4 w-4" />{grouping ? `By ${table.getColumn(grouping)?.columnDef.meta?.label ?? grouping}` : 'Group'}</ToolButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-xl">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-[color:var(--oa-muted)]">Group rows by</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={grouping} onValueChange={(v) => table.setGrouping(v ? [v] : [])}>
                <DropdownMenuRadioItem value="">No grouping</DropdownMenuRadioItem>
                {groupable.map((c) => <DropdownMenuRadioItem key={c.id} value={c.id}>{c.columnDef.meta?.label ?? c.id}</DropdownMenuRadioItem>)}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <ToolButton><Columns3 className="h-4 w-4" />Columns</ToolButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-xl">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-[color:var(--oa-muted)]">Show columns</DropdownMenuLabel>
              {hideable.map((c) => (
                <DropdownMenuCheckboxItem key={c.id} checked={c.getIsVisible()} onCheckedChange={(v) => c.toggleVisibility(!!v)} onSelect={(e) => e.preventDefault()}>
                  {c.columnDef.meta?.label ?? c.id}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => { resetLayout(); setColumnPinning(PINNED); }} className="gap-2 text-[color:var(--oa-muted)]"><RotateCcw className="h-3.5 w-3.5" /> Reset layout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <ToolButton onClick={() => setDensity(prefs.density === 'compact' ? 'comfortable' : 'compact')} title="Toggle row height">
            <Rows3 className="h-4 w-4" />{prefs.density === 'compact' ? 'Compact' : 'Roomy'}
          </ToolButton>
          <ToolButton onClick={() => exportRows(false)} title="Download the current view as CSV"><Download className="h-4 w-4" />Export</ToolButton>
          <ToolButton onClick={() => load(true)} disabled={loading} title="Reload from the database"><RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} /></ToolButton>
        </div>
      </header>

      <FilterBar
        table={table}
        editing={editingFilter}
        onEditingChange={setEditingFilter}
        onReset={() => {
          url.clearAll();
          setRowSelection({});
        }}
      />

      <CatalogueTable table={table} density={prefs.density} loading={loading} onFilterColumn={setEditingFilter} onExpandStyle={loadVariants} loadingStyles={loadingStyles} />

      <SelectionBar styleCodes={selectedCodes} onExport={() => exportRows(true)} onClear={() => setRowSelection({})} />
    </div>
  );
};

export default Catalogue;
